import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app = null;

function getFirebaseAuth() {
  if (!firebaseConfig.apiKey) return null;
  if (!app) app = initializeApp(firebaseConfig);
  return getAuth(app);
}

export const isFirebaseConfigured = () => Boolean(firebaseConfig.apiKey);

// Popups fail (black/blank screen) inside mobile & in-app browsers, so we fall
// back to the redirect flow on those devices.
function isMobileBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent
  );
}

// Opens Google sign-in. On desktop it uses a popup and returns the Firebase ID
// token. On mobile it starts a full-page redirect and never returns (the token
// is picked up by consumeGoogleRedirect() when the app reloads).
export async function signInWithGoogle() {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured");
  await setPersistence(auth, browserLocalPersistence);
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  if (isMobileBrowser()) {
    sessionStorage.setItem("sfx_google_redirect", "1");
    await signInWithRedirect(auth, provider);
    return null; // page navigates away
  }

  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken();
}

// Called on app load. Returns the Firebase ID token if the user just came back
// from a Google redirect sign-in, otherwise null.
export async function consumeGoogleRedirect() {
  if (typeof sessionStorage === "undefined") return null;
  if (!sessionStorage.getItem("sfx_google_redirect")) return null;
  sessionStorage.removeItem("sfx_google_redirect");
  const auth = getFirebaseAuth();
  if (!auth) return null;
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) return result.user.getIdToken();
  } catch {
    /* ignore */
  }
  return null;
}
